/**
 * Land-use names written on the map ("Đất ở hiện có", "Đất cây xanh khu ở", …) for the planning
 * layer on screen. Anchors come from GeoJSON tiles of the official system (pole of inaccessibility
 * of each cell, see engine queryLabelTile): z14 tiles for the zone / detailed / general plans, z12
 * tiles for the 100-year master plan (895, from zoom 15 – its cells are many and small).
 * The transport view writes right-of-way widths instead (rowlabels.js). A label shows only from the zoom where its cell is large enough on screen; the cell area is
 * added from zoom 17.
 * The anchors of all of Hà Nội are pre-computed by scripts/build-labels.mjs (public/data/labels,
 * a few KB per tile instead of 1–8 MB of official GeoJSON); tiles missing there are computed live.
 */
import { api } from './api.js';
import { bus, prefs, savePrefs } from './state.js';
import { tilesForBBox } from './core/geo.js';
import { vurl } from './assetv.js';
import { shortId, decodeLabelTile, parseLabelManifest, labelTileState } from './core/labeltile.js';

/** plan -> label tiles zoom, layers, first zoom with labels, muted tone */
const SPECS = {
  654: { z: 14, layers: [654], min: 14 },
  673: { z: 14, layers: [673], min: 14 },
  666: { z: 14, layers: [666], min: 14 },
  667: { z: 14, layers: [667], min: 14 },
  895: { z: 12, layers: [895], min: 15 },
};
export const LABEL_PLANS = new Set(Object.keys(SPECS).map((k) => (Number.isFinite(+k) ? +k : k)));
const specOf = (plan) => SPECS[plan] || null;
const NEED_PX = 24; // inscribed radius (px) before a cell gets its label
const tiles = new Map(); // "z/x/y" -> labels[]
const loading = new Map();
let pm = null, timer = 0, gen = 0;

const nf1 = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });

/** Map label text: drop long qualifiers the panel already shows in full. */
export function shortName(n) {
  let s = String(n || '').replace(/\s+/g, ' ').trim();
  s = s.replace(/\s*-\s*(quy hoạch|qh)\s*(đợt đầu|dài hạn|giai đoạn.*)$/i, '');
  if (s.length > 34) s = s.replace(/\s*\((?:[^()]*)\)\s*/g, ' ').trim();
  if (s.length > 44) s = s.slice(0, 42).replace(/[\s,;]+\S*$/, '') + '…';
  return s;
}
const areaText = (a) => (a == null ? '' : a >= 10000 ? nf1.format(a / 10000) + ' ha' : nf0.format(a) + ' m²');

export const labelsOn = () => prefs.labels !== false;

/* ---------------- pre-computed tiles ---------------- */

let man; // undefined: not loaded yet, null: unavailable, else the parsed manifest
let manP = null;
function loadManifest() {
  if (man !== undefined) return Promise.resolve(man);
  return (manP ||= fetch(vurl('/data/labels/manifest.json', 'labels'))
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => (man = parseLabelManifest(m)))
    .catch(() => (man = null)));
}
/** Date of the pre-computed labels (ISO), or null. */
export const labelsBuilt = () => man?.generated || null;
async function loadTile(z, x, y) {
  await loadManifest();
  const s = labelTileState(man, z, x, y);
  if (s === 'empty') return [];
  if (s === 'have') {
    try {
      const r = await fetch(`/data/labels/${z}/${x}/${y}.json?v=${man.v}`);
      if (r.ok) return decodeLabelTile(await r.json());
    } catch { /* computed live below */ }
  }
  const r = await api.labels(x, y, z);
  return (r.labels || []).map((l) => ({ id: shortId(l.id), l: l.l, n: l.n, p: l.p, r: l.r, a: l.a }));
}

export function setLabels(on) {
  prefs.labels = !!on;
  savePrefs();
  bus.emit('labels', prefs.labels);
  refresh(true);
}

export function initLabels(planMap) {
  pm = planMap;
  const m = pm.map;
  m.on('moveend', () => schedule(120));
  bus.on('plan', () => refresh(true));
  m.on('style.load', () => refresh(true));
  schedule(0);
}

function schedule(ms) { clearTimeout(timer); timer = setTimeout(() => refresh(false), ms); }

function minZoomFor(r, lat, min) {
  if (!(r > 0)) return 99;
  const c = (40075016.686 * Math.cos((lat * Math.PI) / 180) * NEED_PX) / 512;
  return Math.max(min, Math.log2(c / r));
}

const active = (sp) => sp && labelsOn() && pm.map.getZoom() >= sp.min - 0.5;

/** Labels of one tile for a spec (zone plan first, general plan where the tile has none). */
function pick(list, sp) {
  let out = list.filter((l) => sp.layers.includes(l.l));
  if (!out.length && sp.fallback) out = list.filter((l) => l.l === sp.fallback);
  return sp.skip ? out.filter((l) => !sp.skip.test(l.n || '')) : out;
}

function render() {
  if (!pm) return;
  const sp = specOf(pm.ov.plan);
  if (!active(sp)) { pm.setData('labels', null); return; }
  const best = new Map();
  for (const [k, list] of tiles) {
    if (!k.startsWith(sp.z + '/')) continue;
    for (const l of pick(list, sp)) {
      const prev = best.get(l.id);
      if (!prev || l.r > prev.r) best.set(l.id, l);
    }
  }
  const features = [];
  for (const l of best.values()) {
    const mz = minZoomFor(l.r, l.p[1], sp.min);
    if (mz > 19.5) continue;
    features.push({
      type: 'Feature',
      properties: { t: shortName(l.n), a: sp.muted ? '' : areaText(l.a), r: l.r, mz: Math.round(mz * 100) / 100, m: !!sp.muted },
      geometry: { type: 'Point', coordinates: l.p },
    });
  }
  pm.setData('labels', { type: 'FeatureCollection', features });
}

function refresh(force) {
  if (!pm?.map) return;
  const m = pm.map;
  const sp = specOf(pm.ov.plan);
  if (!active(sp)) { render(); return; }
  const b = m.getBounds();
  const want = tilesForBBox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()], sp.z).slice(0, 30);
  ++gen;
  for (const t of want) {
    const k = `${sp.z}/${t.x}/${t.y}`;
    if (tiles.has(k) || loading.has(k)) continue;
    const p = loadTile(sp.z, t.x, t.y).then((list) => {
      tiles.set(k, list);
      if (tiles.size > 220) tiles.delete(tiles.keys().next().value);
      requestAnimationFrame(render);
    }).catch(() => {}).finally(() => loading.delete(k));
    loading.set(k, p);
  }
  if (force || want.some((t) => tiles.has(`${sp.z}/${t.x}/${t.y}`))) render();
}
